import { FC, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useDebounce } from "../../hooks/useDebounce";
import { UserType } from "../../interfaces/endpoints/IUser.types";
import { issueOperatorApiKey } from "../../services/OperatorApiKeys.service";
import { getPagedUsers } from "../../services/Users.service";
import Button from "../ui/Button";
import Input from "../ui/input/Input";
import Modal from "../ui/Modal";
import IssuedKey from "./IssuedKey";

type Props = {
  show: boolean;
  onClose: () => void;
  onIssued: () => void;
};

// Issues a key for one operator user. Once issued, the key is shown this one time and only "Mbyll" closes the
// dialog, so a tap on the backdrop cannot throw it away.
const IssueKeyModal: FC<Props> = ({ show, onClose, onIssued }) => {
  const [name, setName] = useState("");
  const [search, setSearch] = useState("");
  const [users, setUsers] = useState<UserType[]>([]);
  const [userId, setUserId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [issuedKey, setIssuedKey] = useState<string | null>(null);
  const keyword = useDebounce(search, 400);

  useEffect(() => {
    if (!show) return;
    getPagedUsers({ searchKeyword: keyword, pageIndex: 1, pageSize: 50, userRole: 0 })
      .then((response) => {
        const operatorUsers = (response.data.result ?? []).filter((user) => !!user.operatorId);
        setUsers(operatorUsers);
        setUserId((current) => (operatorUsers.some((user) => user.id === current) ? current : null));
      })
      .catch(() => setUsers([]));
  }, [show, keyword]);

  const close = () => {
    setName("");
    setSearch("");
    setUserId(null);
    setIssuedKey(null);
    onClose();
  };

  const issue = async () => {
    if (!userId || !name.trim()) return;
    setSaving(true);
    try {
      const response = await issueOperatorApiKey({ userAccountId: userId, name: name.trim() });
      setIssuedKey(response.data.key);
      onIssued();
    } catch {
      toast.error("Çelësi nuk u krijua");
    } finally {
      setSaving(false);
    }
  };

  if (issuedKey) {
    return (
      <Modal show={show} position="center" fitContent hasHeader={false}>
        <div className="space-y-4 p-4">
          <IssuedKey value={issuedKey} />
          <div className="flex justify-end">
            <Button onClick={close}>Mbyll</Button>
          </div>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      show={show}
      onClose={close}
      title="Çelës i ri"
      position="center"
      fitContent
      buttonElement={
        <Button onClick={() => void issue()} disabled={saving || !userId || !name.trim()}>
          {saving ? "Duke krijuar…" : "Krijo"}
        </Button>
      }
    >
      <div className="space-y-4 p-4">
        <Input label="Emri" name="key-name" value={name} onChange={(event) => setName(event.target.value)} />
        <Input label="Përdoruesi" name="key-user" placeholder="Kërko sipas email-it" value={search}
          onChange={(event) => setSearch(event.target.value)} />
        {!!users.length && (
          <ul className="max-h-60 divide-y overflow-auto rounded-lg border">
            {users.map((user) => (
              <li key={user.id}>
                <label className="flex cursor-pointer items-center gap-3 px-3 py-2 text-sm hover:bg-gray-50">
                  <input type="radio" name="operator-user" checked={userId === user.id}
                    onChange={() => setUserId(user.id)} />
                  <span className="min-w-0 flex-1 truncate" title={user.userName}>{user.userName}</span>
                  <span className="shrink-0 text-neutral">{user.operator?.operatorName}</span>
                </label>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Modal>
  );
};

export default IssueKeyModal;
